/*
 * MIT License
 *
 * Copyright (c) 2023 Yanael Barbier
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import { sanitizeStreamContentForADFStreamer } from './utils';

export class JsonCloser {
	private buffer = '';
	private stack: string[] = [];
	private objectKeyComplete = true;
	private openSquareBracketsFor: Array<'content' | 'other'> = [];

	private prevSize = 0;

	private closedObject = false;
	private closedArray = false;

	constructor() {}

	append(chunk: string) {
		for (const char of chunk) {
			this.buffer += char;

			switch (char) {
				case '{':
					// If last stack is double quote, then `{` is part of textual content
					if (this.stack[this.stack.length - 1] !== '"') {
						if (this.stack[this.stack.length - 1] === ':') {
							this.stack.pop();
						}
						this.stack.push(char);
						this.closedObject = false;
					}
					break;
				case '}':
					// If last stack is double quote, then `}` is part of textual content
					if (this.stack[this.stack.length - 1] !== '"') {
						if (this.stack[this.stack.length - 1] === '{') {
							this.stack.pop();
						}
						this.closedObject = true;
						this.objectKeyComplete = true;
					}
					break;
				case '[':
					// If last stack is double quote, then `[` is part of textual content
					if (this.stack[this.stack.length - 1] !== '"') {
						if (this.stack[this.stack.length - 1] === ':') {
							this.stack.pop();
						}
						this.stack.push(char);
						this.closedArray = false;
						this.openSquareBracketsFor.push(
							this.isOpenSquareBracketForContent() ? 'content' : 'other',
						);
					}
					break;
				case ']':
					// If last stack is double quote, then `]` is part of textual content
					if (this.stack[this.stack.length - 1] !== '"') {
						if (this.stack[this.stack.length - 1] === '[') {
							this.stack.pop();
						}
						this.openSquareBracketsFor.pop();
					}
					break;
				case '"':
					// If last two chars backslash and double quote, then `"` is part of textual content
					if (this.buffer.slice(-2) === '\\"') {
						break;
					}

					if (this.stack[this.stack.length - 1] === ':') {
						this.stack.pop();
					}
					if (this.stack[this.stack.length - 1] === '"') {
						this.stack.pop();
					} else {
						this.stack.push(char);
					}
					const secondLastCharter = this.buffer[this.buffer.length - 2];
					if (secondLastCharter === '{' || secondLastCharter === ',') {
						this.objectKeyComplete = false;
					}
					break;
				case ':':
					if (this.stack[this.stack.length - 1] !== '"') {
						this.objectKeyComplete = true;
						if (this.buffer[this.buffer.length - 2] === '"') {
							this.stack.push(char);
						}
					}
					break;
				default:
					/**
					 * Don't remove colon from stack if char is space, otherwise it will be invlaid JSON.
					 */
					if (this.stack[this.stack.length - 1] === ':' && char !== ' ') {
						this.stack.pop();
					}
					break;
			}
		}
	}

	closeJson(): string {
		let closeBuffer = this.buffer.trim();

		for (const char of [...this.stack].reverse()) {
			switch (char) {
				case '{':
					if (closeBuffer[closeBuffer.length - 1] === ',') {
						closeBuffer = closeBuffer.slice(0, -1);
					} else if (closeBuffer[closeBuffer.length - 1] === '{') {
						this.objectKeyComplete = true;
					} else if (!this.objectKeyComplete) {
						this.objectKeyComplete = true;
						closeBuffer += ':""';
					}

					closeBuffer += '}';
					break;
				case '[':
					if (closeBuffer[closeBuffer.length - 1] === ',') {
						closeBuffer = closeBuffer.slice(0, -1);
					}
					closeBuffer += ']';
					break;
				case '"':
					if (
						closeBuffer[closeBuffer.length - 1] === '"' &&
						closeBuffer[closeBuffer.length - 2] === '{'
					) {
						closeBuffer = closeBuffer.slice(0, -1);
					} else {
						closeBuffer += '"';
					}
					break;
				case ':':
					/**
					 * Valid JSON values are string, number, boolean, object, array or null.
					 * object, array, string and number value is already handled.
					 *
					 * So if last character is "t" for true, "f" for false or "n" for null
					 *  then it can be still valid JSON value. When next stream arrives, it can
					 * 	complete boolean or null value.
					 *
					 * Currently we have not handled this case as ADF JSON don't have boolean or null values.
					 */
					closeBuffer += '""';
					break;
				default:
					break;
			}
		}

		return closeBuffer;
	}

	parse() {
		const closedJson = sanitizeStreamContentForADFStreamer(this.closeJson());
		const jsonRes = JSON.parse(closedJson);

		const size = JSON.stringify(jsonRes).length;

		let hasChanged = false;

		if (size > this.prevSize) {
			this.prevSize = size;
			hasChanged = true;
		}

		// Do not process twice if the array and the object get closed.
		// XOR operation to check if either the array or object get closed but not both
		else if (this.closedObject !== this.closedArray) {
			// If the object have been closed consider it as a change
			// If the array have been close the object have been closed too
			// No need to consider it as a change
			// This is to avoid processing twice the same completion
			hasChanged = this.closedObject;
			this.closedObject = false;
		}

		return {
			hasChanged,
			closedJson: jsonRes,
			stack: this.stack,
			openSquareBracketsFor: this.openSquareBracketsFor,
		};
	}

	isOpenSquareBracketForContent() {
		let startDoubleQuoteIndex = -1;
		let endDoubleQuoteIndex = -1;
		let isSquareBracketForValue = false;
		for (let i = this.buffer.length - 1; i >= 0; i--) {
			const char = this.buffer[i];

			/**
			 * Color has to be preceded by square bracket for it to
			 * 	be considered as a value
			 */
			if (char === ':') {
				isSquareBracketForValue = true;
			}
			if (isSquareBracketForValue && char === '"') {
				if (endDoubleQuoteIndex === -1) {
					endDoubleQuoteIndex = i;
				} else {
					startDoubleQuoteIndex = i;
					break;
				}
			}
		}
		return this.buffer.slice(startDoubleQuoteIndex + 1, endDoubleQuoteIndex) === 'content';
	}

	reset() {
		this.buffer = '';
		this.stack = [];
		this.prevSize = 0;
		this.closedObject = false;
		this.closedArray = false;
		this.openSquareBracketsFor = [];
		this.objectKeyComplete = true;
	}
}
